// mesbot/src/institution/pages/Overview.js
import React, { useEffect, useState } from "react";
import { getWellness, getOverview } from "../api";

function StatCard({ label, value, delta, deltaType = "positive" }) {
  if (value === null || value === undefined) {
    return (
      <div className="inst-card">
        <div className="inst-card-label">{label}</div>
        <div className="inst-no-data">Not enough data</div>
      </div>
    );
  }
  return (
    <div className="inst-card">
      <div className="inst-card-label">{label}</div>
      <div className="inst-card-value">{value.toLocaleString()}</div>
      {delta !== undefined && (
        <div className={`inst-card-delta ${deltaType === "neutral" ? "neutral" : ""}`}>
          {delta}
        </div>
      )}
    </div>
  );
}

function TopicBar({ label, pct, color }) {
  if (pct === null || pct === undefined) {
    return (
      <div>
        <div className="inst-topic-bar-header">
          <span className="inst-topic-name">{label}</span>
          <span className="inst-no-data">—</span>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="inst-topic-bar-header">
        <span className="inst-topic-name">{label}</span>
        <span className="inst-topic-pct">{pct}%</span>
      </div>
      <div className="inst-topic-track">
        <div className="inst-topic-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function barColor(pct) {
  if (pct === null) return "#9ca3af";
  if (pct >= 40) return "#f59e0b";
  return "#4ade80";
}

export default function Overview({ token }) {
  const [overview, setOverview] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    getOverview(token).then(setOverview).catch(e => setError(e.message));
    getWellness(token, 2).then(setSnapshots).catch(() => {});
  }, [token]);

  const last7 = snapshots.slice(0, 7).reverse();
  const latestSnap = snapshots[0];

  const weekDelta = overview
    ? overview.sessions_last_week > 0
      ? `${overview.sessions_this_week > overview.sessions_last_week ? "+" : ""}${
          overview.sessions_this_week - overview.sessions_last_week
        } vs last week`
      : null
    : null;

  return (
    <>
      <div className="inst-page-title">Overview</div>
      <div className="inst-page-subtitle">
        {overview?.last_updated
          ? `Last updated: ${new Date(overview.last_updated).toLocaleDateString()}`
          : "Data updates daily at 02:00 UTC"}
      </div>

      {error && <div className="inst-error">{error}</div>}

      <div className="inst-card-grid">
        <StatCard label="Total Students" value={overview?.total_students} />
        <StatCard
          label="Active This Week"
          value={overview?.active_this_week}
          delta={overview ? `${Math.round((overview.active_this_week / (overview.total_students || 1)) * 100)}% of enrolled` : null}
          deltaType="neutral"
        />
        <StatCard
          label="Sessions This Week"
          value={overview?.sessions_this_week}
          delta={weekDelta}
        />
      </div>

      <div className="inst-row-2">
        <div className="inst-panel">
          <div className="inst-panel-title">Campus Wellness Temperature</div>
          <div className="inst-panel-subtitle">Session volume this week</div>
          <div className="inst-bar-chart">
            {last7.length === 0 ? (
              <div className="inst-no-data">No data yet</div>
            ) : last7.map((snap) => {
              const maxSessions = Math.max(...last7.map(s => s.total_sessions), 1);
              const heightPct = (snap.total_sessions / maxSessions) * 100;
              const stressPct = snap.topic_stress_pct;
              return (
                <div key={snap.snapshot_date} className="inst-bar-col">
                  <div
                    className="inst-bar"
                    style={{
                      height: `${heightPct}%`,
                      background: barColor(stressPct),
                    }}
                  />
                  <div className="inst-bar-label">
                    {new Date(snap.snapshot_date).toLocaleDateString("en", { weekday: "short" }).slice(0, 1)}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 12, fontSize: 10 }}>
            <span style={{ color: "#4ade80" }}>● Calm</span>
            <span style={{ color: "#f59e0b" }}>● Elevated</span>
          </div>
        </div>

        <div className="inst-panel">
          <div className="inst-panel-title">Top Topics This Week</div>
          <div className="inst-panel-subtitle">Aggregated — no individual data</div>
          <div className="inst-topic-bar-row">
            <TopicBar label="Academic Stress" pct={latestSnap?.topic_academic_pct} color="#4f46e5" />
            <TopicBar label="Anxiety" pct={latestSnap?.topic_anxiety_pct} color="#7c3aed" />
            <TopicBar label="Loneliness" pct={latestSnap?.topic_loneliness_pct} color="#ec4899" />
            <TopicBar label="Stress" pct={latestSnap?.topic_stress_pct} color="#f59e0b" />
            <TopicBar label="Other" pct={latestSnap?.topic_other_pct} color="#9ca3af" />
          </div>
        </div>
      </div>
    </>
  );
}
